import { Layout, Dropdown, Avatar, Button } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CreditCardOutlined,
  AuditOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
// Stage 6B.9H — page-title table + resolver extracted to a pure-JS
// sidecar so node:test can exercise the topbar mapping. AppHeader
// retains the JSX-only chrome.
import { pageTitleFromPath } from './appHeaderPageTitles';

const { Header } = Layout;

// Top app bar — brand-blue gradient, subtle bottom border, sidebar
// toggle + current page title on the left, tenant / role / user
// pills on the right. The brand mark lives in the sidebar so the
// topbar stays uncluttered.

function avatarInitials(email) {
  if (!email) return '?';
  const local = String(email).split('@')[0] || '';
  return local.slice(0, 2).toUpperCase();
}

export default function AppHeader({ collapsed, onToggleCollapsed }) {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = pageTitleFromPath(location.pathname);

  const role = me && me.role ? me.role : null;
  const isMaster = me && me.isMaster === true;

  const items = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: (
        <div className="py-1">
          <div className="font-medium text-ink-900 text-sm">
            {me ? me.email : 'guest'}
          </div>
          <div className="text-xs text-ink-500">
            {isMaster ? 'master account' : role || 'sub-user'}
          </div>
        </div>
      ),
      disabled: true
    },
    { type: 'divider' },
    // Stage 5K-5B: discoverable entry for /profile so masters and
    // sub-users can reach the profile drawer (Edit profile, Change my
    // password, Activity) from anywhere in the app.
    {
      key: 'my-profile',
      icon: <UserOutlined />,
      label: 'My profile',
      onClick: () => navigate('/profile')
    },
    {
      key: 'session',
      icon: <AuditOutlined />,
      label: 'Session activity',
      onClick: () => navigate('/session')
    },
    isMaster
      ? {
          key: 'subscription',
          icon: <CreditCardOutlined />,
          label: 'Subscription',
          onClick: () => navigate('/subscription')
        }
      : null,
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Log out',
      danger: true,
      onClick: () => logout()
    }
  ].filter(Boolean);

  return (
    <Header
      style={{
        backgroundColor: '#1877F2',
        // Refined enterprise depth — a barely-perceptible radial wash
        // on the left, a top hairline highlight, and a soft bottom
        // shadow line that together give the solid brand blue
        // dimensionality without becoming a gradient. Same atmosphere
        // Stripe / Linear use on their dark chrome.
        backgroundImage:
          'radial-gradient(ellipse 800px 240px at 0% 0%, rgba(255,255,255,0.10), transparent 60%)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        width: '100%',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.10), 0 1px 0 rgba(15,23,42,0.06), 0 4px 16px -8px rgba(15,23,42,0.25)'
      }}
      className="!px-4 sm:!px-6 border-b border-white/10 flex items-center justify-between"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggleCollapsed}
          aria-label="Toggle navigation"
          className="!h-9 !w-9 !flex items-center justify-center !rounded-lg !text-white/90 hover:!bg-white/10 hover:!text-white shrink-0"
        />
        {/* Vertical hairline divider — separates the toggle from the
            page title so the topbar reads as two distinct zones. */}
        {pageTitle ? (
          <span
            className="inline-block h-5 w-px bg-white/20 ml-1 shrink-0"
            aria-hidden="true"
          />
        ) : null}
        {/* Current page title — gives users a clear "you are here"
            anchor without depending on the sidebar highlight. Falls
            back to nothing on routes we don't title-map. */}
        {pageTitle ? (
          <h1 className="text-white font-semibold text-[15.5px] tracking-[-0.005em] m-0 truncate">
            {pageTitle}
          </h1>
        ) : null}
      </div>

      <div className="flex items-center gap-4">
        {/* Tenant — clean inline icon + name (no pill bg). The
            tenant identity is the most repeated element on the page;
            wrapping it in a heavy pill made the topbar feel crowded.
            Letting it sit flat against the gradient keeps the rhythm
            light and reserves pill backgrounds for interactive
            elements (role + avatar). */}
        {me && (me.tenantDisplayName || me.tenantSlug) ? (
          <span className="hidden md:inline-flex items-center gap-2 text-[12.5px]">
            <ShopOutlined className="text-white/75" aria-hidden="true" />
            <span className="text-white font-semibold tracking-tight uppercase max-w-[200px] truncate">
              {me.tenantDisplayName || me.tenantSlug}
            </span>
          </span>
        ) : null}

        {/* Vertical divider — separates the tenant identity from
            the interactive cluster (role + avatar). */}
        <span
          className="hidden md:inline-block h-5 w-px bg-white/20"
          aria-hidden="true"
        />

        {/* Role chip — small bordered pill that reads as a status
            label, not a button. Slightly stronger border + inset
            highlight than a plain translucent fill so the badge
            reads as an enterprise role marker, not a UI tint. */}
        <span
          className="hidden sm:inline-flex items-center h-7 px-3 rounded-md text-[10.5px] font-semibold uppercase tracking-[0.14em] text-white bg-white/[0.12] border border-white/25"
          style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)' }}
        >
          {isMaster ? 'Master' : (role || 'viewer')}
        </span>

        {/* User dropdown trigger — circular avatar only. Username
            and dropdown caret intentionally omitted; the full email
            and role still render inside the dropdown menu. Soft halo
            on hover + active-state press feedback give the chip a
            tactile, app-like quality. */}
        <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
          <button
            type="button"
            title={me ? me.email : undefined}
            aria-label="Open user menu"
            className="group inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/25 hover:border-white/50 hover:bg-white/10 active:scale-[0.97] transition-[transform,background-color,border-color] duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10)' }}
          >
            <Avatar
              size={28}
              className="!bg-white !text-brand-700 !font-bold !text-[11px] group-hover:!shadow-[0_0_0_2px_rgba(255,255,255,0.18)] transition-shadow duration-150"
            >
              {avatarInitials(me ? me.email : null)}
            </Avatar>
          </button>
        </Dropdown>
      </div>
    </Header>
  );
}
